import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashValues from 'lodash/fp/values';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { FRONT_INBOX_TYPES } from 'common/frontConstants';
import {
  SR_API_ACTION_ADMIN_GET_FRONT_CHANNELS,
  SR_API_ACTION_ADMIN_ADD_FRONT_CHANNEL,
  SR_API_ACTION_ADMIN_REMOVE_FRONT_CHANNEL,
} from 'api/actionTypes';
import { SCREEN_ID_ADMIN_FRONT_CHANNELS } from 'client/constants/screenIds';

import Navbar from 'client/components/Navbar';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import H1 from 'client/components/H1';
import SrFormikField from 'client/components/SrFormikField';

import branch from 'client/core/branch';
import MarketActions from 'client/actions/Market';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withScreenId from 'client/helpers/withScreenId';
import allowedRoles from 'client/helpers/allowedRoles';
import postWithState from 'client/actions/postWithState';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

function createFormFields({ marketList } = {}) {
  const marketChoices = (marketList || []).map((market) => ({
    label: market.displayName,
    value: market.id,
  }));

  const formFields = [
    {
      id: 'phone',
      title: 'twilio phone',
      type: 'phone',
      properties: {
        placeholder: '5121234567',
      },
      validations: ['required', 'phone'],
    },
    {
      id: 'id',
      title: 'front channel id',
      type: 'short_text',
      properties: {
        placeholder: '',
      },
      validations: ['required'],
    },
    {
      id: 'marketId',
      title: 'Market',
      type: 'select',
      properties: {
        choices: marketChoices,
        multipleSelection: false,
        allowEmptyArray: false,
      },
      validations: ['required'],
    },
    {
      id: 'inboxType',
      title: 'Inbox Type',
      type: 'select',
      properties: {
        choices: FRONT_INBOX_TYPES.map((inboxType) => ({
          label: inboxType,
          value: inboxType,
        })),
        multipleSelection: false,
        allowEmptyArray: false,
      },
      validations: ['required'],
    },
  ];

  return formFields;
}

const formInitialState = createInitialValues({
  formFields: createFormFields(),
  defaultValues: {},
});

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScrollToTop
@withScreenId(SCREEN_ID_ADMIN_FRONT_CHANNELS)
@withApiState({
  addFrontChannelApiState: {
    action: SR_API_ACTION_ADMIN_ADD_FRONT_CHANNEL,
  },
  removeFrontChanelApiState: {
    action: SR_API_ACTION_ADMIN_REMOVE_FRONT_CHANNEL,
  },
})
@branch({
  marketsById: ['markets', 'byId'],
})
class AdminFrontChannelsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin Front Channels | Sunroom Rentals',
    section: 'Admin',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    addFrontChannelApiState: PropTypes.object.isRequired,
    removeFrontChanelApiState: PropTypes.object.isRequired,
    marketsById: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([MarketActions.getList(inject)]);
  }

  constructor(props) {
    super(props);

    this.state = {
      frontChannels: [],
    };
  }

  componentDidMount() {
    this._loadFrontChannels();
  }

  _onAddFrontChannel = (values, { resetForm }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_ADD_FRONT_CHANNEL,
      payload: {
        ...values,
        marketId: parseInt(values.marketId, 10),
      },
    }).then(() => {
      resetForm(formInitialState);
      this._loadFrontChannels();
    });
  };

  _loadFrontChannels = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_GET_FRONT_CHANNELS,
    }).then((frontChannels) => {
      this.setState({ frontChannels });
    });
  };

  _removeFrontChannel = (frontChannel) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REMOVE_FRONT_CHANNEL,
      payload: {
        id: frontChannel.id,
      },
    }).then(() => {
      this._loadFrontChannels();
    });
  };

  render() {
    const { frontChannels } = this.state;
    const { addFrontChannelApiState, removeFrontChanelApiState, marketsById } = this.props;

    const formFields = createFormFields({
      marketList: lodashValues(marketsById),
    });

    return (
      <div className="page-admin">
        <div className="adminContainer">
          <Navbar redirectLogoTo="/admin" />

          <div className="contentContainer">
            <Formik
              validateOnChange
              initialValues={formInitialState}
              validate={createFormValidationFn(formFields)}
              onSubmit={this._onAddFrontChannel}
              render={(formikBag) => {
                return (
                  <div>
                    <div className="header">
                      <H1>Add new front channel</H1>
                    </div>
                    <Form>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                        />
                      ))}
                      <div className="bottomActions">
                        <ButtonDeprecated
                          type="submit"
                          disabled={!formikBag.isValid || addFrontChannelApiState.inProgress}
                        >
                          {addFrontChannelApiState.inProgress ? 'adding ...' : 'add'}
                        </ButtonDeprecated>
                      </div>
                    </Form>
                  </div>
                );
              }}
            />
            {removeFrontChanelApiState.error && (
              <div className="errorMessage">{removeFrontChanelApiState.error.message}</div>
            )}
          </div>

          <div className="frontChannelList">
            {(frontChannels || []).map((frontChannel) => (
              <div key={frontChannel.id} className={`${frontChannel.id}`}>
                <span>{frontChannel.id}</span>
                <span> | </span>
                <span>{frontChannel.phone}</span>
                <span> | </span>
                <span>{(marketsById[`${frontChannel.marketId}`] || {}).displayName}</span>
                <span
                  onClick={() => this._removeFrontChannel(frontChannel)}
                  style={{ cursor: 'pointer' }}
                >
                  <span role="img" aria-label="delete-front-channel-icon">
                    🙅
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

AdminFrontChannelsPage.displayName = 'AdminFrontChannelsPage';

export default AdminFrontChannelsPage;
